import React, { useCallback, useState } from 'react';
import { ERROR_MESSAGES, toUserMessage } from '../../utils/userErrors';
import AppLoader from '../../components/modules/AppLoader';
import apiService from '../../utils/apiService';
import { useFetchOnceByKey } from '../../hooks/useMountOnce';
import { generateQrDataUrl } from './qrHelpers';
import QrStudio from './QrStudio';
import '../../styles/module-shared.css';
import './qr-code.css';

const pick = (obj, ...keys) => {
  for (const key of keys) {
    if (obj?.[key] != null && String(obj[key]).trim() !== '') return String(obj[key]).trim();
  }
  return '';
};

const PartnerQr = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [partnerUrl, setPartnerUrl] = useState('');
  const [affiliateCode, setAffiliateCode] = useState('');
  const [qrSource, setQrSource] = useState('url');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrGenerating, setQrGenerating] = useState(false);
  const [qrError, setQrError] = useState('');
  const [qrLabel, setQrLabel] = useState('');

  const fetchPartnerLink = useCallback(async ({ cancelled }) => {
    setLoading(true);
    setError('');
    try {
      const [referRes, profileRes] = await Promise.all([
        apiService.getReferSubPartners(),
        apiService.getUserProfile(),
      ]);
      if (cancelled()) return;

      const referData = referRes?.data?.result ?? referRes?.data ?? referRes?.result ?? {};
      const referralLink = pick(referData, 'referralLink', 'ReferralLink');
      setPartnerUrl(referralLink);

      if (profileRes?.success && profileRes.data) {
        const code = pick(profileRes.data, 'affiliateCode', 'AffiliateCode');
        setAffiliateCode(code);
        if (!referralLink && code) {
          setError('Partner referral URL is not available yet.');
        }
      }

      if (!referRes?.success && !referralLink) {
        setError(referRes?.error || 'Failed to load partner referral URL');
      }
    } catch (err) {
      if (!cancelled()) setError(toUserMessage(err, ERROR_MESSAGES.load));
    } finally {
      if (!cancelled()) setLoading(false);
    }
  }, []);

  useFetchOnceByKey('partner-qr-link', fetchPartnerLink, true);

  const handleSourceChange = (source) => {
    setQrSource(source);
    setQrDataUrl('');
    setQrLabel('');
    setQrError('');
  };

  const handleGenerateQr = async () => {
    const payload = qrSource === 'code' ? affiliateCode : partnerUrl;
    if (!payload) {
      setQrError(
        qrSource === 'code'
          ? 'Partner code is not available.'
          : 'Partner URL is not available.'
      );
      setQrDataUrl('');
      setQrLabel('');
      return;
    }

    setQrGenerating(true);
    setQrError('');
    try {
      const dataUrl = await generateQrDataUrl(payload);
      setQrDataUrl(dataUrl);
      setQrLabel(payload);
    } catch {
      setQrDataUrl('');
      setQrLabel('');
      setQrError('Failed to generate QR code. Please try again.');
    } finally {
      setQrGenerating(false);
    }
  };

  if (loading) return <AppLoader />;

  return (
    <QrStudio
      title="Partner QR"
      subtitle="Generate a premium QR from your partner code or partner referral URL"
      codeLabel="Partner Code"
      urlLabel="Partner URL"
      codeValue={affiliateCode}
      urlValue={partnerUrl}
      qrSource={qrSource}
      onSourceChange={handleSourceChange}
      onGenerate={handleGenerateQr}
      qrGenerating={qrGenerating}
      qrError={qrError}
      qrDataUrl={qrDataUrl}
      qrLabel={qrLabel}
      downloadName={`partner-qr-${qrSource}.png`}
      error={error}
    />
  );
};

export default PartnerQr;
